import { AuthError, AuthErrorKind } from '@client/auth/auth-error';
import { AuthSession } from '@client/auth/interfaces/auth-session.interface';
import { EntraClientConfig } from '@client/auth/interfaces/entra-client-config.interface';
import { AuthBridgeError, AuthBridgeResult, PrivanotAuthAPI } from '@client/electron/electron-main';
import { EntraAuthProvider, TokenRequestOptions } from './entra-auth-provider';

export interface BrowserPages {
    successHtml: string;
    errorHtml: string;
}

const KINDS: Record<AuthBridgeError, AuthErrorKind> = {
    unreachable: 'unreachable',
    access_denied: 'accessDenied',
    cancelled: 'cancelled',
    interaction_required: 'sessionExpired',
    unknown: 'unknown',
};

const unwrap = (result: AuthBridgeResult): AuthSession => {
    if (!result.ok) {
        throw new AuthError(KINDS[result.error] ?? 'unknown');
    }
    return result.session;
};

/** Desktop: sign-in runs in the Electron main process (system browser); this side only relays over IPC. */
export class ElectronEntraAuthProvider implements EntraAuthProvider {
    constructor(
        private readonly config: EntraClientConfig,
        private readonly bridge: PrivanotAuthAPI,
        private readonly pages: () => BrowserPages,
    ) {}

    // The main process keeps its session across a renderer reload, but not across launches.
    public async initialize(): Promise<AuthSession | null> {
        const result = await this.bridge.getToken();
        return result.ok ? result.session : null;
    }

    public async signIn(): Promise<AuthSession> {
        const result = await this.bridge.signIn({
            tenantId: this.config.tenantId ?? '',
            clientId: this.config.clientId ?? '',
            scope: this.config.scope ?? '',
            ...this.pages(),
        });
        return unwrap(result);
    }

    public async getToken(options?: TokenRequestOptions): Promise<AuthSession> {
        return unwrap(await this.bridge.getToken(options));
    }

    public signOut(): Promise<void> {
        return this.bridge.signOut();
    }

    public cancel(): Promise<void> {
        return this.bridge.cancel();
    }
}
